import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { ArrowLeft, Calendar, Target, Users, TrendingUp, Award, CheckCircle2, XCircle } from "lucide-react";
import { format, parseISO, eachDayOfInterval, isSameDay, differenceInDays } from "date-fns";

import { ChallengeComments } from "./ChallengeComments";
import { ChallengeActivity } from "./ChallengeActivity";
import { ParticipantReactions } from "./ParticipantReactions";
import { ChallengeAnalytics } from "./ChallengeAnalytics";
import { ProgressChart } from "./ProgressChart";
import { GroupMessaging } from "./GroupMessaging";
import { TeamLeaderboard } from "./TeamLeaderboard";

interface ChallengeDetailProps {
  challengeId: string;
  onBack: () => void;
}

interface Participant {
  id: string;
  user_id: string;
  progress_data: {
    completed_days: number;
    total_logged_days: number;
    daily_progress: {
      [date: string]: {
        completed: boolean;
        value?: number;
      };
    };
  };
}

export function ChallengeDetail({ challengeId, onBack }: ChallengeDetailProps) {
  const { data: challenge, isLoading: challengeLoading } = useQuery({
    queryKey: ['challenge-detail', challengeId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('group_challenges')
        .select('*')
        .eq('id', challengeId)
        .single();
      
      if (error) throw error;
      return data;
    },
  });

  const { data: participants, isLoading: participantsLoading } = useQuery({
    queryKey: ['challenge-participants', challengeId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('challenge_participants')
        .select('id, user_id, progress_data')
        .eq('challenge_id', challengeId)
        .eq('completion_status', 'active');
      
      if (error) throw error;
      return data as Participant[];
    },
  });

  const { data: user } = useQuery({
    queryKey: ['user'],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      return user;
    },
  });

  if (challengeLoading || participantsLoading) {
    return (
      <Card className="p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-muted rounded w-1/3"></div>
          <div className="h-4 bg-muted rounded w-2/3"></div>
        </div>
      </Card>
    );
  }

  if (!challenge) {
    return (
      <Card className="p-6">
        <p className="text-muted-foreground">Challenge not found</p>
      </Card>
    );
  }

  const startDate = parseISO(challenge.start_date);
  const endDate = parseISO(challenge.end_date);
  const allDays = eachDayOfInterval({ start: startDate, end: endDate });
  const today = new Date();
  const daysRemaining = Math.max(0, differenceInDays(endDate, today));
  
  const myParticipation = participants?.find(p => p.user_id === user?.id);
  const myProgress = myParticipation?.progress_data || {
    completed_days: 0,
    total_logged_days: 0,
    daily_progress: {}
  };

  const completionRate = myProgress.total_logged_days > 0 
    ? Math.round((myProgress.completed_days / myProgress.total_logged_days) * 100)
    : 0;

  const currentStreak = calculateStreak(myProgress.daily_progress);
  const longestStreak = calculateLongestStreak(myProgress.daily_progress);

  const goalCriteria = challenge.goal_criteria as any;
  const habitType = goalCriteria?.habit_type;
  const requiredDays = goalCriteria?.required_days || allDays.length;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start gap-4">
        <Button variant="ghost" size="icon" onClick={onBack}>
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div className="flex-1">
          <div className="flex items-start justify-between mb-2">
            <div>
              <h2 className="text-2xl font-bold">{challenge.title}</h2>
              <p className="text-muted-foreground mt-1">{challenge.description}</p>
            </div>
            <Badge variant={daysRemaining > 0 ? "default" : "secondary"}>
              {daysRemaining > 0 ? `${daysRemaining} days left` : 'Completed'}
            </Badge>
          </div>
          <div className="flex gap-2 mt-3">
            <Badge variant="outline">{challenge.challenge_type}</Badge>
            {habitType && <Badge variant="outline">{getHabitLabel(habitType)}</Badge>}
          </div>
        </div>
      </div>

      {/* Stats Overview */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-primary/10 rounded-lg">
              <CheckCircle2 className="h-5 w-5 text-primary" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Completed</p>
              <p className="text-2xl font-bold">{myProgress.completed_days}/{requiredDays}</p>
            </div>
          </div>
        </Card>

        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-primary/10 rounded-lg">
              <TrendingUp className="h-5 w-5 text-primary" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Success Rate</p>
              <p className="text-2xl font-bold">{completionRate}%</p>
            </div>
          </div>
        </Card>

        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-primary/10 rounded-lg">
              <Award className="h-5 w-5 text-primary" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Current Streak</p>
              <p className="text-2xl font-bold">{currentStreak} days</p>
            </div>
          </div>
        </Card>

        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-primary/10 rounded-lg">
              <Users className="h-5 w-5 text-primary" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Participants</p>
              <p className="text-2xl font-bold">{participants?.length || 0}</p>
            </div>
          </div>
        </Card>
      </div>

      {/* Progress Bar */}
      <Card className="p-6">
        <div className="space-y-3">
          <div className="flex justify-between text-sm">
            <span className="font-medium">Overall Progress</span>
            <span className="text-muted-foreground">{myProgress.completed_days} of {requiredDays} days</span>
          </div>
          <Progress value={(myProgress.completed_days / requiredDays) * 100} className="h-3" />
        </div>
      </Card>

      {/* Analytics & Progress Chart */}
      {myProgress.daily_progress && Object.keys(myProgress.daily_progress).length > 0 && (
        <div className="grid md:grid-cols-2 gap-6">
          <ChallengeAnalytics
            progressData={{
              completed_days: myProgress.completed_days,
              total_logged_days: myProgress.total_logged_days,
              daily_values: Object.entries(myProgress.daily_progress).map(([date, data]) => ({
                date,
                value: data.completed ? 1 : 0,
                goal_met: data.completed,
              })),
            }}
            challengeType={challenge.challenge_type}
            goalCriteria={challenge.goal_criteria}
            participantCount={participants?.length || 0}
          />
          <ProgressChart
            dailyValues={Object.entries(myProgress.daily_progress).map(([date, data]) => ({
              date,
              value: data.completed ? 1 : 0,
              goal_met: data.completed,
            }))}
            challengeType={challenge.challenge_type}
          />
        </div>
      )}

      {/* Daily Calendar */}
      <Card className="p-6">
        <div className="flex items-center gap-2 mb-4">
          <Calendar className="h-5 w-5" />
          <h3 className="text-lg font-semibold">Daily Progress Calendar</h3>
        </div>
        <div className="grid grid-cols-7 gap-2">
          {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
            <div key={day} className="text-center text-xs font-medium text-muted-foreground p-2">
              {day}
            </div>
          ))}
          {allDays.map((day) => {
            const dateStr = format(day, 'yyyy-MM-dd');
            const dayProgress = myProgress.daily_progress[dateStr];
            const isPast = day < today;
            const isToday = isSameDay(day, today);
            const isFuture = day > today;
            
            let bgColor = 'bg-muted';
            let icon = null;
            
            if (dayProgress?.completed) {
              bgColor = 'bg-primary text-primary-foreground';
              icon = <CheckCircle2 className="h-4 w-4" />;
            } else if (dayProgress && !dayProgress.completed) {
              bgColor = 'bg-destructive/20 text-destructive';
              icon = <XCircle className="h-4 w-4" />;
            } else if (isFuture) {
              bgColor = 'bg-muted/50';
            }

            return (
              <div
                key={dateStr}
                className={`
                  aspect-square rounded-lg p-2 flex flex-col items-center justify-center
                  ${bgColor}
                  ${isToday ? 'ring-2 ring-primary ring-offset-2' : ''}
                  transition-all
                `}
              >
                <span className="text-xs font-medium">{format(day, 'd')}</span>
                {icon && <div className="mt-1">{icon}</div>}
              </div>
            );
          })}
        </div>
        <div className="flex gap-4 mt-4 text-xs text-muted-foreground">
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded bg-primary"></div>
            <span>Completed</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded bg-destructive/20"></div>
            <span>Missed</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded bg-muted"></div>
            <span>Not logged</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded bg-muted/50"></div>
            <span>Upcoming</span>
          </div>
        </div>
      </Card>

      {/* Participants Leaderboard */}
      <Card className="p-6">
        <div className="flex items-center gap-2 mb-4">
          <Users className="h-5 w-5" />
          <h3 className="text-lg font-semibold">Participants ({participants?.length || 0})</h3>
        </div>
        <div className="space-y-3">
          {participants
            ?.sort((a, b) => {
              const aCompleted = a.progress_data?.completed_days || 0;
              const bCompleted = b.progress_data?.completed_days || 0;
              return bCompleted - aCompleted;
            })
            .map((participant, index) => {
              const progress = participant.progress_data || { completed_days: 0, total_logged_days: 0, daily_progress: {} };
              const participantRate = progress.total_logged_days > 0
                ? Math.round((progress.completed_days / progress.total_logged_days) * 100)
                : 0;
              const participantStreak = calculateStreak(progress.daily_progress);
              const isMe = participant.user_id === user?.id;

              return (
                <div
                  key={participant.user_id}
                  className={`space-y-3 p-3 rounded-lg border ${
                    isMe ? 'border-primary bg-primary/5' : ''
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="flex items-center gap-2">
                        <span className="text-lg font-semibold text-muted-foreground">#{index + 1}</span>
                        <Avatar className="h-10 w-10">
                          <AvatarFallback>
                            {isMe ? 'You' : `U${index + 1}`}
                          </AvatarFallback>
                        </Avatar>
                      </div>
                      <div>
                        <p className="font-medium">{isMe ? 'You' : `User ${index + 1}`}</p>
                        <p className="text-sm text-muted-foreground">
                          {progress.completed_days} days • {participantStreak} day streak
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-semibold text-lg">{participantRate}%</p>
                      <p className="text-xs text-muted-foreground">success rate</p>
                    </div>
                  </div>
                  
                  {/* Reactions */}
                  {!isMe && (
                    <div className="border-t pt-3">
                      <p className="text-xs text-muted-foreground mb-2">Encourage this participant:</p>
                      <ParticipantReactions
                        challengeId={challengeId}
                        participantId={participant.id}
                        userId={participant.user_id}
                      />
                    </div>
                  )}
                </div>
              );
            })}
        </div>
      </Card>

      {/* Activity Feed & Comments */}
      <div className="grid md:grid-cols-2 gap-6">
        <ChallengeActivity challengeId={challengeId} />
        <ChallengeComments challengeId={challengeId} />
      </div>

      {/* Team Leaderboard */}
      <TeamLeaderboard challengeId={challengeId} />

      {/* Group Chat */}
      <GroupMessaging challengeId={challengeId} />
    </div>
  );
}

function calculateStreak(dailyProgress: { [date: string]: { completed: boolean } }): number {
  const dates = Object.keys(dailyProgress).sort().reverse();
  let streak = 0;
  const today = format(new Date(), 'yyyy-MM-dd');
  
  for (const date of dates) {
    if (date > today) continue;
    if (dailyProgress[date]?.completed) {
      streak++;
    } else {
      break;
    }
  }
  
  return streak;
}

function calculateLongestStreak(dailyProgress: { [date: string]: { completed: boolean } }): number {
  const dates = Object.keys(dailyProgress).sort();
  let maxStreak = 0;
  let currentStreak = 0;
  
  for (const date of dates) {
    if (dailyProgress[date]?.completed) {
      currentStreak++;
      maxStreak = Math.max(maxStreak, currentStreak);
    } else {
      currentStreak = 0;
    }
  }
  
  return maxStreak;
}

function getHabitLabel(habitType: string): string {
  const labels: Record<string, string> = {
    'alcohol_free': 'Alcohol-Free',
    'no_smoking': 'Smoke-Free',
    'water_intake': 'Hydration',
    'screen_time': 'Screen Time',
    'gratitude': 'Gratitude',
    'exercise': 'Exercise',
    'sleep_8h': 'Sleep 8h',
    'sunlight': 'Sunlight',
  };
  return labels[habitType] || habitType;
}
